import React, { useEffect, useState } from "react";
import {
  TrendingUp,
  Users,
  Layers,
  Wallet,
  Image as ImageIcon,
  CreditCard,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import Chart from "react-apexcharts";
import axiosInstance from "../../utils/axios";

// ===================== SKELETON LOADER =====================
const DashboardSkeleton = () => {
  return (
    <div className="w-full p-4 sm:p-6 space-y-6 animate-pulse">
      {/* KPI Skeleton */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="bg-white shadow-sm rounded-lg p-4 flex justify-between"
          >
            <div className="space-y-2 w-full">
              <div className="h-3 w-20 bg-gray-300 rounded"></div>
              <div className="h-5 w-28 bg-gray-300 rounded"></div>
            </div>
            <div className="h-10 w-10 bg-gray-300 rounded-md"></div>
          </div>
        ))}
      </div>

      {/* Chart Skeleton */}
      <div className="bg-white rounded-lg p-5 shadow-sm h-[320px]">
        <div className="h-6 w-40 bg-gray-300 rounded mb-4"></div>
        <div className="h-[250px] w-full bg-gray-200 rounded"></div>
      </div>

      <div className="bg-white rounded-lg p-5 shadow-sm h-[320px]">
        <div className="h-6 w-40 bg-gray-300 rounded mb-4"></div>
        <div className="h-[250px] w-full bg-gray-200 rounded"></div>
      </div>

      {/* Table Skeleton */}
      <div className="bg-white rounded-lg p-5 shadow-sm">
        <div className="h-6 w-40 bg-gray-300 rounded mb-4"></div>
        {[...Array(4)].map((_, i) => (
          <div key={i} className="w-full h-12 bg-gray-200 rounded mb-2"></div>
        ))}
      </div>

      {/* Gallery Skeleton */}
      <div className="bg-white rounded-lg p-5 shadow-sm">
        <div className="h-6 w-48 bg-gray-300 rounded mb-4"></div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="w-full h-28 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===================== ERROR SCREEN =====================
const DashboardError = ({ onRetry }) => {
  return (
    <div className="w-full h-[60vh] flex flex-col items-center justify-center text-center p-6">
      <AlertTriangle className="w-16 h-16 text-red-500 mb-4" />
      <h2 className="text-2xl font-bold text-primaryTextColor mb-2">
        Something went wrong
      </h2>
      <p className="text-secondaryTextColor mb-5">
        We couldn't load the dashboard. Please try again.
      </p>
      <button
        onClick={onRetry}
        className="flex items-center gap-2 bg-primaryColor hover:bg-primaryBtnHoverColor text-white px-5 py-2 rounded-md"
      >
        <RefreshCw className="w-5 h-5" /> Retry
      </button>
    </div>
  );
};

// ===================== MAIN DASHBOARD =====================
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchStats = async () => {
    try {
      setError(false);
      setLoading(true);

      const res = await axiosInstance.get("/dashboard/stats");
      setStats(res.data);
    } catch (err) {
      console.log("Dashboard Error:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // SHOW SKELETON WHILE LOADING
  if (loading) return <DashboardSkeleton />;

  // SHOW ERROR SCREEN
  if (error || !stats) return <DashboardError onRetry={fetchStats} />;

  // If loaded ✨
  const {
    kpis,
    donationTrend,
    campaignPerformance,
    latestDonations,
    recentGallery,
  } = stats;

  // ===================== KPI CARDS =====================
  const KPIS = [
    {
      title: "Total Donations",
      value: `₹ ${kpis.totalDonations}`,
      icon: <Wallet className="w-7 h-7 text-primaryColor" />,
    },
    {
      title: "Total Users",
      value: kpis.usersCount,
      icon: <Users className="w-7 h-7 text-primaryColor" />,
    },
    {
      title: "Total Campaigns",
      value: kpis.campaignsCount,
      icon: <Layers className="w-7 h-7 text-primaryColor" />,
    },
    {
      title: "Transactions",
      value: kpis.transactionsCount,
      icon: <CreditCard className="w-7 h-7 text-primaryColor" />,
    },
    {
      title: "Gallery Images",
      value: kpis.galleryCount,
      icon: <ImageIcon className="w-7 h-7 text-primaryColor" />,
    },
  ];

  // ===================== DONATION CHART =====================
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const donationChart = {
    options: {
      chart: { toolbar: { show: false } },
      colors: ["#02323F"],
      stroke: { curve: "smooth", width: 3 },
      grid: { strokeDashArray: 4 },
      xaxis: {
        categories: donationTrend.map((m) => monthNames[m._id - 1]),
      },
    },
    series: [{ name: "Donations", data: donationTrend.map((d) => d.total) }],
  };

  // ===================== CAMPAIGN PERFORMANCE CHART =====================
  const campaignPerformanceChart = {
    options: {
      chart: { type: "bar", toolbar: { show: false } },
      colors: ["#8cc441"],
      plotOptions: { bar: { borderRadius: 6, columnWidth: "45%" } },
      xaxis: {
        categories: campaignPerformance.map((c) => c.name),
      },
    },
    series: [
      { name: "Collected", data: campaignPerformance.map((c) => c.collected) },
    ],
  };

  return (
    <div className="w-full p-4 sm:p-6 space-y-6">
      {/* KPI CARDS */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        {KPIS.map((item, index) => (
          <div
            key={index}
            className="bg-white shadow-sm rounded-lg p-4 flex items-center justify-between hover:shadow-md transition"
          >
            <div>
              <p className="text-secondaryTextColor text-sm">{item.title}</p>
              <h2 className="text-primaryTextColor text-xl font-bold mt-1">
                {item.value}
              </h2>
            </div>
            <div className="p-2 bg-primaryFadedColor rounded-md">
              {item.icon}
            </div>
          </div>
        ))}
      </div>

      {/* DONATION TREND */}
      <div className="bg-white rounded-lg p-5 shadow-sm">
        <h2 className="text-[20px] font-bold text-primaryTextColor mb-3 flex gap-2">
          <TrendingUp className="w-5 h-5" /> Donation Trend
        </h2>

        <Chart
          options={donationChart.options}
          series={donationChart.series}
          type="area"
          height={320}
        />
      </div>

      {/* CAMPAIGN PERFORMANCE */}
      <div className="bg-white rounded-lg p-5 shadow-sm">
        <h2 className="text-[20px] font-bold text-primaryTextColor mb-3">
          Campaign Performance
        </h2>

        <Chart
          options={campaignPerformanceChart.options}
          series={campaignPerformanceChart.series}
          type="bar"
          height={320}
        />
      </div>

      {/* LATEST DONATIONS */}
      <div className="bg-white rounded-lg p-5 shadow-sm">
        <h2 className="text-[20px] font-bold text-primaryTextColor mb-4">
          Latest Donations
        </h2>

        <div className="overflow-x-auto noScrollbar">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-primaryFadedColor text-primaryTextColor text-left">
                <th className="p-3">Donor</th>
                <th className="p-3">Amount</th>
                <th className="p-3">Program</th>
                <th className="p-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {latestDonations.map((d, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="p-3">{d.userId?.name || "Anonymous"}</td>
                  <td className="p-3 font-semibold text-primaryColor">
                    ₹ {d.amount}
                  </td>
                  <td className="p-3">{d.adId?.name || d.adId?.serviceName}</td>
                  <td className="p-3">
                    {new Date(d.date).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* GALLERY */}
      <div className="bg-white rounded-lg p-5 shadow-sm">
        <h2 className="text-[20px] font-bold text-primaryTextColor mb-4">
          Recent Gallery Uploads
        </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {recentGallery.map((img) => (
            <div
              key={img._id}
              className="w-full h-28 bg-gray-200 rounded-lg overflow-hidden"
            >
              <img
                src={img.image}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

// import React from "react";
// import {
//   TrendingUp,
//   Users,
//   Layers,
//   Wallet,
//   Image as ImageIcon,
//   CreditCard,
// } from "lucide-react";
// import Chart from "react-apexcharts";

// const Dashboard = () => {
//   // ---------------------- MOCK DATA ---------------------
//   const KPIS = [
//     {
//       title: "Total Donations",
//       value: "₹ 12,45,200",
//       icon: <Wallet className="w-7 h-7 text-primaryColor" />,
//     },
//     {
//       title: "Total Users",
//       value: "3,482",
//       icon: <Users className="w-7 h-7 text-primaryColor" />,
//     },
//     {
//       title: "Total Campaigns",
//       value: "18",
//       icon: <Layers className="w-7 h-7 text-primaryColor" />,
//     },
//     {
//       title: "Transactions",
//       value: "1,279",
//       icon: <CreditCard className="w-7 h-7 text-primaryColor" />,
//     },
//     {
//       title: "Gallery Images",
//       value: "92",
//       icon: <ImageIcon className="w-7 h-7 text-primaryColor" />,
//     },
//   ];

//   // ---------------------- DONATION TREND CHART ---------------------
//   const donationChart = {
//     options: {
//       chart: {
//         id: "donations",
//         toolbar: { show: false },
//       },
//       colors: ["#02323F"],
//       stroke: {
//         curve: "smooth",
//         width: 3,
//       },
//       grid: { strokeDashArray: 4 },
//       xaxis: {
//         categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
//       },
//     },
//     series: [
//       {
//         name: "Donations",
//         data: [12000, 15000, 18000, 22000, 17000, 26000, 30000],
//       },
//     ],
//   };

//   // ---------------------- CAMPAIGN PERFORMANCE ---------------------
//   const campaignPerformance = {
//     options: {
//       chart: {
//         type: "bar",
//         toolbar: { show: false },
//       },
//       colors: ["#8cc441"],
//       plotOptions: {
//         bar: { horizontal: false, columnWidth: "45%", borderRadius: 6 },
//       },
//       xaxis: {
//         categories: ["Health", "Education", "Women Empowerment", "Food Drive"],
//       },
//     },
//     series: [
//       {
//         name: "Goal Achieved (%)",
//         data: [70, 55, 80, 40],
//       },
//     ],
//   };

//   // ---------------------- LATEST DONATIONS (MOCK) ---------------------
//   const latestDonations = [
//     {
//       name: "Rohit Sharma",
//       amount: 1200,
//       campaign: "Food Drive",
//       date: "2 Nov 2024",
//     },
//     {
//       name: "Anjali Verma",
//       amount: 5000,
//       campaign: "Women Empowerment",
//       date: "31 Oct 2024",
//     },
//     {
//       name: "Karan Mehta",
//       amount: 2200,
//       campaign: "Health",
//       date: "30 Oct 2024",
//     },
//   ];

//   // ---------------------- RECENT GALLERY ---------------------
//   const gallery = [
//     "/images/sample1.jpg",
//     "/images/sample2.jpg",
//     "/images/sample3.jpg",
//     "/images/sample4.jpg",
//     "/images/sample5.jpg",
//     "/images/sample6.jpg",
//   ];

//   return (
//     <div className="w-full p-4 sm:p-6 space-y-6">
//       {/* ---------------------- KPI CARDS ---------------------- */}
//       <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
//         {KPIS.map((item, index) => (
//           <div
//             key={index}
//             className="bg-white shadow-sm rounded-lg p-4 flex items-center justify-between hover:shadow-md transition"
//           >
//             <div>
//               <p className="text-secondaryTextColor text-sm">{item.title}</p>
//               <h2 className="text-primaryTextColor text-xl font-bold mt-1">
//                 {item.value}
//               </h2>
//             </div>
//             <div className="p-2 bg-primaryFadedColor rounded-md">
//               {item.icon}
//             </div>
//           </div>
//         ))}
//       </div>

//       {/* ---------------------- DONATION TREND ---------------------- */}
//       <div className="bg-white rounded-lg p-5 shadow-sm">
//         <div className="flex justify-between items-center mb-3">
//           <h2 className="text-[20px] font-bold text-primaryTextColor flex items-center gap-2">
//             <TrendingUp className="w-5 h-5" /> Donation Trend
//           </h2>
//         </div>

//         <Chart
//           options={donationChart.options}
//           series={donationChart.series}
//           type="area"
//           height={320}
//         />
//       </div>

//       {/* ---------------------- CAMPAIGN PERFORMANCE ---------------------- */}
//       <div className="bg-white rounded-lg p-5 shadow-sm">
//         <h2 className="text-[20px] font-bold text-primaryTextColor mb-3">
//           Campaign Performance
//         </h2>

//         <Chart
//           options={campaignPerformance.options}
//           series={campaignPerformance.series}
//           type="bar"
//           height={320}
//         />
//       </div>

//       {/* ---------------------- LATEST DONATIONS TABLE ---------------------- */}
//       <div className="bg-white rounded-lg p-5 shadow-sm">
//         <h2 className="text-[20px] font-bold text-primaryTextColor mb-4">
//           Latest Donations
//         </h2>

//         <div className="overflow-x-auto noScrollbar">
//           <table className="w-full border-collapse">
//             <thead>
//               <tr className="bg-primaryFadedColor text-primaryTextColor text-left">
//                 <th className="p-3">Donor</th>
//                 <th className="p-3">Amount</th>
//                 <th className="p-3">Campaign</th>
//                 <th className="p-3">Date</th>
//               </tr>
//             </thead>
//             <tbody>
//               {latestDonations.map((d, i) => (
//                 <tr key={i} className="border-b hover:bg-gray-50">
//                   <td className="p-3">{d.name}</td>
//                   <td className="p-3 font-semibold text-primaryColor">
//                     ₹ {d.amount}
//                   </td>
//                   <td className="p-3">{d.campaign}</td>
//                   <td className="p-3">{d.date}</td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       {/* ---------------------- GALLERY ---------------------- */}
//       <div className="bg-white rounded-lg p-5 shadow-sm">
//         <h2 className="text-[20px] font-bold text-primaryTextColor mb-4">
//           Recent Gallery Uploads
//         </h2>

//         <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
//           {gallery.map((img, i) => (
//             <div
//               key={i}
//               className="w-full h-28 bg-gray-200 rounded-lg overflow-hidden shadow-sm"
//             >
//               <img src={img} alt="" className="w-full h-full object-cover" />
//             </div>
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Dashboard;

// // import Chart from "react-apexcharts";
// // import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";

// // const Dashboard = () => {
// //   const chartOptions = {
// //     chart: {
// //       type: "area",
// //       height: "100%",
// //       width: "100%",
// //       toolbar: {
// //         show: false,
// //       },
// //     },
// //     responsive: [
// //       {
// //         breakpoint: 640,
// //         options: {
// //           chart: {
// //             height: 300,
// //           },
// //         },
// //       },
// //       {
// //         breakpoint: 1024,
// //         options: {
// //           chart: {
// //             height: 400,
// //           },
// //         },
// //       },
// //     ],
// //     dataLabels: {
// //       enabled: false,
// //     },
// //     stroke: {
// //       curve: "smooth",
// //     },
// //     xaxis: {
// //       categories: [
// //         "Jan",
// //         "Feb",
// //         "Mar",
// //         "Apr",
// //         "May",
// //         "Jun",
// //         "Jul",
// //         "Aug",
// //         "Sep",
// //         "Oct",
// //         "Nov",
// //         "Dec",
// //       ],
// //       title: {
// //         text: "Months",
// //       },
// //     },
// //     yaxis: {
// //       title: {
// //         text: "Expenses",
// //       },
// //     },
// //   };

// //   const chartSeries = [
// //     {
// //       name: "Monthly Expenses",
// //       data: [450, 380, 600, 700, 400, 900, 750, 650, 800, 700, 500, 450],
// //     },
// //   ];

// //   return (
// //     <div className="">
// //       <AdminSearchBar
// //         title={"Dashboard"}
// //         hideAddButton={true}
// //         hideSearchButton={true}
// //       />
// //       <div className="chart sm:h-[30rem] h-[26rem] bg-white rounded-lg p-3 sm:p-6 ">
// //         <div className="flex justify-between items-center">
// //           <h1 className="text-[18px] text-primaryTextColor sm:text-[24px] font-bold">
// //             Monthly Expenses Details
// //           </h1>
// //           <select name="" id="" className="p-2 border-2 rounded-md">
// //             <option value="">Current Month</option>
// //             <option value="">January</option>
// //             <option value="">February</option>
// //             <option value="">March</option>
// //             <option value="">April</option>
// //             <option value="">May</option>
// //             <option value="">June</option>
// //             <option value="">July</option>
// //             <option value="">August</option>
// //             <option value="">September</option>
// //             <option value="">October</option>
// //             <option value="">November</option>
// //             <option value="">December</option>
// //           </select>
// //         </div>

// //         <Chart
// //           options={chartOptions}
// //           series={chartSeries}
// //           type="area"
// //           height="100%"
// //         />
// //       </div>
// //     </div>
// //   );
// // };

// // export default Dashboard;
